import type { AxiosRequestConfig } from 'axios';
import type {
  CsatReportFilters,
  CsatReportListResponse,
  CsatReportQuery,
  CsatReportSummary,
} from '@troia-v3/types';

import { api } from '@/lib/api';

/**
 * A planilha passou do teto do servidor (10.000 atendimentos) e veio só com
 * os mais recentes. Lançado DEPOIS do download — quem chama só avisa.
 */
export class CsatExportTruncatedError extends Error {
  constructor() {
    super('A planilha trouxe os 10.000 atendimentos mais recentes. Refine os filtros para ver o restante.');
    this.name = 'CsatExportTruncatedError';
  }
}

/** Query string sem chave vazia — filtro ausente não vai como `?channelId=`. */
const toParams = (query: CsatReportFilters | CsatReportQuery): Record<string, string | number> => {
  const params: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params[key] = value as string | number;
  }
  return params;
};

/** Relatórios → CSAT (`/api/reports/csat`, permissão `reports-csat`). */
export const csatReportApi = {
  async list(query: CsatReportQuery): Promise<CsatReportListResponse> {
    const response = await api.get('reports/csat', { params: toParams(query) });
    return response.data.data;
  },

  async summary(filters: CsatReportFilters): Promise<CsatReportSummary> {
    const response = await api.get('reports/csat/summary', { params: toParams(filters) });
    return response.data.data;
  },

  /**
   * Baixa o CSV do recorte. O fuso do navegador vai junto: a planilha mostra
   * os mesmos horários que a tela.
   */
  async exportCsv(filters: CsatReportFilters): Promise<void> {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const config: AxiosRequestConfig & { _handleErrorLocally: boolean } = {
      params: { ...toParams(filters), ...(timezone ? { timezone } : {}) },
      responseType: 'blob',
      // A tela avisa do erro — sem isto o aviso global sairia junto (e, com
      // resposta em blob, sem a mensagem do servidor).
      _handleErrorLocally: true,
    };
    const response = await api.get('reports/csat/export', config);

    let filename = `csat-${new Date().toISOString().split('T')[0]}.csv`;
    const disposition = response.headers['content-disposition'] as string | undefined;
    const match = disposition ? /filename="?([^";]+)"?/.exec(disposition) : null;
    if (match?.[1]) filename = match[1];

    const blob = new Blob([response.data as BlobPart], { type: 'text/csv; charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    if (response.headers['x-export-truncated'] === 'true') {
      throw new CsatExportTruncatedError();
    }
  },
};
